import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const expandedCalendarVariants = cva('p-3 pb-0 rounded-[12px] bg-white min-h-[calc(100vh-150px)] transition-all flex flex-col shadow-dropshadow-sm animate-fade-in', {
  variants: {
    variant: {
      default: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

const ExpandedCalendar = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(expandedCalendarVariants({ variant }), className)}
    {...props}
  />
));
ExpandedCalendar.displayName = 'ExpandedCalendar';

const ExpandedCalendarHeader = React.forwardRef(({ className, variant, ...props }, ref) => (
  <header
    ref={ref}
    className={cn('flex items-center justify-between gap-3 py-5 relative border-b border-solid border-neutral-lighter', className)}
    {...props}
  />
));
ExpandedCalendarHeader.displayName = 'ExpandedCalendarHeader';

const ExpandedCalendarButtonVariants = cva(
  'appearance-none bg-transparent border-none p-0 text-left cursor-pointer inline-flex items-center gap-2 transition-all duration-200 ease-in-out px-[38px] py-2 text-neutral hover:text-primary-dark disabled:pointer-events-none disabled:opacity-24 [&>svg]:w-4 [&>svg]:h-4',
  {
    variants: {
      variant: {
        default: '',
        equalFill: 'p-2'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

const ExpandedCalendarButton = React.forwardRef(({ className, variant, label, onClick, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(ExpandedCalendarButtonVariants({ variant }), className)}
    aria-label={label}
    onClick={onClick}
    {...props}
  />
));
ExpandedCalendarButton.displayName = 'ExpandedCalendarButton';

const ExpandedCalendarButtonText = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('hidden sm:inline', className)}
    aria-hidden
    {...props}
  />
));
ExpandedCalendarButtonText.displayName = 'ExpandedCalendarButtonText';

const ExpandedCalendarSelectedWeek = React.forwardRef(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn('text-primary-dark font-bold text-sm mb-0 absolute left-1/2 -translate-x-1/2 text-center', className)}
    {...props}
  />
));
ExpandedCalendarSelectedWeek.displayName = 'ExpandedCalendarSelectedWeek';

const ExpandedCalendarBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex-grow flex flex-col overflow-auto lg:flex-row', className)}
    {...props}
  />
));
ExpandedCalendarBody.displayName = 'ExpandedCalendarBody';

const expandedCalendarDayVariants = cva('flex-grow flex-shrink-0 w-full py-8 px-1 border-l border-r border-solid border-transparent lg:flex-basis-0 lg:max-w-[calc(100%/7)]', {
  variants: {
    isCurrent: {
      true: 'bg-bright border-l-[#EAEAEA] border-r-[#EAEAEA]'
    }
  },
  defaultVariants: {
    isCurrent: false
  }
});

const ExpandedCalendarDay = React.forwardRef(({ className, isCurrent, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(expandedCalendarDayVariants({ isCurrent }), className)}
    {...props}
  />
));
ExpandedCalendarDay.displayName = 'ExpandedCalendarDay';

const ExpandedCalendarDayHeader = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('flex items-center flex-wrap gap-1 mb-6 text-xs', className)}
    {...props}
  />
));
ExpandedCalendarDayHeader.displayName = 'ExpandedCalendarDayHeader';

const expandedCalendarDayNumberVariants = cva('font-bold text-xs text-neutral-dark', {
  variants: {
    isCurrent: {
      true: 'text-primary-dark'
    }
  },
  defaultVariants: {
    isCurrent: false
  }
});

const ExpandedCalendarDayNumber = React.forwardRef(({ isCurrent, className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(expandedCalendarDayNumberVariants({ isCurrent }), className)}
    {...props}
  />
));
ExpandedCalendarDayNumber.displayName = 'ExpandedCalendarDayNumber';

const expandedCalendarWeekdayVariants = cva('text-neutral text-xs font-light', {
  variants: {
    isCurrent: {
      true: 'text-primary-dark'
    }
  },
  defaultVariants: {
    isCurrent: false
  }
});

const ExpandedCalendarWeekday = React.forwardRef(({ className, isCurrent, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(expandedCalendarWeekdayVariants({ isCurrent }), className)}
    {...props}
  />
));
ExpandedCalendarWeekday.displayName = 'ExpandedCalendarWeekday';

const ExpandedCalendarHoursContainer = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-2 lg:flex lg:flex-col', className)}
    {...props}
  />
));
ExpandedCalendarHoursContainer.displayName = 'ExpandedCalendarHoursContainer';

export {
  ExpandedCalendar,
  ExpandedCalendarHeader,
  ExpandedCalendarButton,
  ExpandedCalendarButtonText,
  ExpandedCalendarSelectedWeek,
  ExpandedCalendarBody,
  ExpandedCalendarDay,
  ExpandedCalendarDayHeader,
  ExpandedCalendarDayNumber,
  ExpandedCalendarWeekday,
  ExpandedCalendarHoursContainer
};
